import { createStripRetimeMap } from './retimeMap'
import {
  getStripRetimePointsLocal,
  getStripRetimePointsNormalized,
} from './retimePoints'

const EPS = 1e-6

const clamp = (value, min, max) => {
  const n = Number(value)
  if (!Number.isFinite(n)) return min
  if (n <= min) return min
  if (n >= max) return max
  return n
}

const round = (value, digits = 6) => {
  const n = Number(value)
  if (!Number.isFinite(n)) return 0
  const p = 10 ** digits
  return Math.round(n * p) / p
}

const toMultiplier = (percent) => {
  const p = Number(percent)
  if (!Number.isFinite(p) || p <= 0) return 1
  return p / 100
}

const toLocalPointsFromNormalized = (points, clipLength) => {
  const len = Math.max(EPS, Number(clipLength) || 0)
  const list = Array.isArray(points) ? points : []
  return list
    .map((p) => ({
      t: clamp((Number(p.u) || 0) * len, 0, len),
      speed: Number.isFinite(Number(p.speed)) && Number(p.speed) > 0 ? Number(p.speed) : 1,
      affects: p?.affects === 'right' ? 'right' : 'left',
    }))
    .sort((a, b) => a.t - b.t)
}

const integrateLinear = (s0, s1, span, localSpan) => {
  if (span <= EPS) return 0
  const x = clamp(localSpan, 0, span)
  if (Math.abs(s1 - s0) <= EPS) return s0 * x
  const k = (s1 - s0) / span
  return s0 * x + 0.5 * k * x * x
}

const normalizeLocalPoints = (points, clipLength) => {
  const len = Math.max(EPS, Number(clipLength) || 0)
  const list = Array.isArray(points) ? points : []
  return list
    .map((p) => ({
      t: clamp(Number(p.t) || 0, 0, len),
      speed: Number.isFinite(Number(p.speed)) && Number(p.speed) > 0 ? Number(p.speed) : 1,
      affects: p?.affects === 'right' ? 'right' : 'left',
    }))
    .sort((a, b) => a.t - b.t)
}

const pointAffects = (p) => (p?.affects === 'right' ? 'right' : 'left')

const stepSegmentsFromLocalPoints = (clipLength, points) => {
  const len = Math.max(EPS, Number(clipLength) || 0)
  const pts = normalizeLocalPoints(points, len)
  if (!pts.length) {
    return [{
      index: 0,
      t0: 0,
      t1: len,
      speed: 1,
      resolvedBy: 'baseline-1x',
    }]
  }
  const boundaries = [0, ...pts.map((p) => p.t), len]
  const segs = []
  for (let i = 0; i < boundaries.length - 1; i++) {
    const t0 = boundaries[i]
    const t1 = boundaries[i + 1]
    const leftPoint = i > 0 ? pts[i - 1] : null
    const rightPoint = i < pts.length ? pts[i] : null
    let speed = 1
    let resolvedBy = 'baseline-1x'
    if (rightPoint && pointAffects(rightPoint) === 'left') {
      speed = Number(rightPoint.speed) > 0 ? Number(rightPoint.speed) : 1
      resolvedBy = `right-boundary-point[${i}] affects:left`
    } else if (leftPoint && pointAffects(leftPoint) === 'right') {
      speed = Number(leftPoint.speed) > 0 ? Number(leftPoint.speed) : 1
      resolvedBy = `left-boundary-point[${i - 1}] affects:right`
    }
    segs.push({ index: i, t0, t1, speed, resolvedBy })
  }
  return segs
}

const linearSegmentsFromLocalPoints = (clipLength, points) => {
  const len = Math.max(EPS, Number(clipLength) || 0)
  const pts = normalizeLocalPoints(points, len)
  if (!pts.length) {
    return [{
      index: 0,
      t0: 0,
      t1: len,
      s0: 1,
      s1: 1,
      resolvedBy: 'baseline-1x',
    }]
  }
  const segs = []
  let idx = 0
  if (pts[0].t > 0) {
    segs.push({
      index: idx++,
      t0: 0,
      t1: pts[0].t,
      s0: pts[0].speed,
      s1: pts[0].speed,
      resolvedBy: 'head-constant-from-point[0]',
    })
  }
  for (let i = 0; i < pts.length - 1; i++) {
    segs.push({
      index: idx++,
      t0: pts[i].t,
      t1: pts[i + 1].t,
      s0: pts[i].speed,
      s1: pts[i + 1].speed,
      resolvedBy: `interpolate-point[${i}]→point[${i + 1}]`,
    })
  }
  if (pts[pts.length - 1].t < len) {
    const lastIdx = pts.length - 1
    segs.push({
      index: idx++,
      t0: pts[lastIdx].t,
      t1: len,
      s0: pts[lastIdx].speed,
      s1: pts[lastIdx].speed,
      resolvedBy: `tail-constant-from-point[${lastIdx}]`,
    })
  }
  return segs
}

const manualSpeedAtLocal = (localTime, clipLength, points, mode = 'linear') => {
  const len = Math.max(EPS, Number(clipLength) || 0)
  const lt = clamp(Number(localTime) || 0, 0, len)
  const pts = normalizeLocalPoints(points, len)
  if (!pts.length) return 1
  if (mode === 'step') {
    const eps = 1e-6
    for (let i = 0; i < pts.length; i++) {
      const p = pts[i]
      if (p.t <= 0 || p.t >= len) continue
      if (Math.abs(lt - p.t) <= eps) return p.speed
    }
    const segs = stepSegmentsFromLocalPoints(len, pts)
    for (let i = 0; i < segs.length; i++) {
      const seg = segs[i]
      const isLast = i === segs.length - 1
      if (lt >= seg.t0 && (lt < seg.t1 || (isLast && lt <= seg.t1))) return seg.speed
    }
    return segs[segs.length - 1]?.speed || 1
  }
  const segs = linearSegmentsFromLocalPoints(len, pts)
  for (let i = 0; i < segs.length; i++) {
    const seg = segs[i]
    if (lt >= seg.t0 && lt <= seg.t1) {
      const span = seg.t1 - seg.t0
      if (span <= EPS || Math.abs(seg.s1 - seg.s0) <= EPS) return seg.s0
      const k = (seg.s1 - seg.s0) / span
      return seg.s0 + (k * (lt - seg.t0))
    }
  }
  return pts[pts.length - 1]?.speed || 1
}

const upsertLocalPoint = (list, point, clipLength) => {
  const len = Math.max(EPS, Number(clipLength) || 0)
  const t = clamp(Number(point?.t) || 0, 0, len)
  const speed = Number.isFinite(Number(point?.speed)) && Number(point?.speed) > 0 ? Number(point.speed) : 1
  const affects = pointAffects(point)
  const idx = list.findIndex((p) => Math.abs((Number(p.t) || 0) - t) <= 1e-6)
  const next = { t, speed, affects }
  if (idx >= 0) list[idx] = next
  else list.push(next)
}

const applyHorizontalPointDragLocal = ({ points, clipLength, index, newT }) => {
  const len = Math.max(EPS, Number(clipLength) || 0)
  const pts = normalizeLocalPoints(points, len)
  if (!pts.length) return null
  const idx = Math.max(0, Math.min(pts.length - 1, Number(index) || 0))
  const p = { ...pts[idx] }
  const leftBound = idx > 0 ? pts[idx - 1].t : 0
  const rightBound = idx < pts.length - 1 ? pts[idx + 1].t : len
  const eps = 1e-4
  const clampedT = clamp(Number(newT) || p.t, leftBound + eps, rightBound - eps)
  const affects = pointAffects(p)

  // Use effective segment speed (resolved via ownership rules), not raw point speed.
  const sampleEps = 1e-5
  const sampleT = affects === 'right'
    ? Math.min(p.t + sampleEps, rightBound - sampleEps)
    : Math.max(p.t - sampleEps, leftBound + sampleEps)
  const effectiveSpeed = manualSpeedAtLocal(sampleT, len, pts, 'step')

  // Determine which point resolves the affected segment.
  // In step mode, rightPoint with affects:'left' wins over leftPoint with affects:'right'.
  let resolverIdx = idx
  if (affects === 'right' && idx < pts.length - 1 && pointAffects(pts[idx + 1]) === 'left') {
    resolverIdx = idx + 1
  }

  let preservedMedia = 0
  let newSpeed = effectiveSpeed
  if (affects === 'right') {
    const oldLocal = Math.max(eps, rightBound - p.t)
    preservedMedia = oldLocal * effectiveSpeed
    const newLocal = Math.max(eps, rightBound - clampedT)
    newSpeed = preservedMedia / newLocal
  } else {
    const oldLocal = Math.max(eps, p.t - leftBound)
    preservedMedia = oldLocal * effectiveSpeed
    const newLocal = Math.max(eps, clampedT - leftBound)
    newSpeed = preservedMedia / newLocal
  }

  // Move dragged point; only update its speed if it is the resolver.
  pts[idx] = {
    t: clampedT,
    speed: resolverIdx === idx ? Math.max(0.01, newSpeed) : p.speed,
    affects,
  }
  // If resolver is a neighbor, update that point's speed instead.
  if (resolverIdx !== idx) {
    pts[resolverIdx] = {
      ...pts[resolverIdx],
      speed: Math.max(0.01, newSpeed),
    }
  }
  return {
    points: normalizeLocalPoints(pts, len),
    affectedSegment: {
      affects,
      leftBound,
      rightBound,
      oldT: p.t,
      newT: clampedT,
      preservedMedia,
    },
  }
}

const sliceWindowPointsLocal = ({ points, clipLength, fromLocal, toLocal, mode = 'step' }) => {
  const len = Math.max(EPS, Number(clipLength) || 0)
  const start = clamp(Number(fromLocal) || 0, 0, len)
  const end = clamp(Number(toLocal) || len, start, len)
  const span = Math.max(0, end - start)
  const pts = normalizeLocalPoints(points, len)
  if (!pts.length || span <= EPS) return []
  const out = []

  pts.forEach((p) => {
    if (p.t > start + EPS && p.t < end - EPS) upsertLocalPoint(out, { ...p, t: p.t - start }, span)
    else if (Math.abs(p.t - start) <= EPS) upsertLocalPoint(out, { ...p, t: 0 }, span)
    else if (Math.abs(p.t - end) <= EPS) upsertLocalPoint(out, { ...p, t: span }, span)
  })

  const sampleStart = mode === 'step' ? Math.min(len, start + 1e-5) : start
  const sampleEnd = mode === 'step' ? Math.max(0, end - 1e-5) : end
  const speedStart = manualSpeedAtLocal(sampleStart, len, pts, mode)
  const speedEnd = manualSpeedAtLocal(sampleEnd, len, pts, mode)
  if (mode === 'step') {
    upsertLocalPoint(out, { t: 0, speed: speedStart, affects: 'right' }, span)
    upsertLocalPoint(out, { t: span, speed: speedEnd, affects: 'left' }, span)
  } else {
    upsertLocalPoint(out, { t: 0, speed: speedStart, affects: 'left' }, span)
    upsertLocalPoint(out, { t: span, speed: speedEnd, affects: 'left' }, span)
  }
  return normalizeLocalPoints(out, span)
}

const evaluateRemapContinuity = ({
  originalPoints,
  originalLength,
  mode = 'step',
  fromLocal,
  toLocal,
  remappedPoints,
}) => {
  const len = Math.max(EPS, Number(originalLength) || 0)
  const start = clamp(Number(fromLocal) || 0, 0, len)
  const end = clamp(Number(toLocal) || len, start, len)
  const span = Math.max(EPS, end - start)
  const base = manualMapLocalToMedia(start, len, originalPoints, mode)
  const checkpoints = Array.from({ length: 9 }).map((_, i) => i / 8)
  const samples = checkpoints.map((u) => {
    const origLocal = start + (u * span)
    const remapLocal = u * span
    const expected = manualMapLocalToMedia(origLocal, len, originalPoints, mode) - base
    const actual = manualMapLocalToMedia(remapLocal, span, remappedPoints, mode)
    const delta = actual - expected
    return {
      u: round(u, 4),
      expectedMediaDelta: round(expected),
      actualMediaDelta: round(actual),
      delta: round(delta),
      pass: Math.abs(delta) <= 5e-4,
    }
  })
  return {
    pass: samples.every((s) => s.pass),
    samples,
  }
}

const monotonicityCheck = ({ clipLength, points, mode = 'step' }) => {
  const len = Math.max(EPS, Number(clipLength) || 0)
  const checkpoints = Array.from({ length: 21 }).map((_, i) => i / 20)
  let prev = -Infinity
  let ok = true
  const values = checkpoints.map((u) => {
    const local = u * len
    const media = manualMapLocalToMedia(local, len, points, mode)
    if (media + 1e-7 < prev) ok = false
    prev = media
    return { u: round(u, 4), media: round(media) }
  })
  return { pass: ok, values }
}

const manualMapLocalToMedia = (localTime, clipLength, points, mode = 'linear') => {
  const len = Math.max(EPS, Number(clipLength) || 0)
  const lt = clamp(localTime, 0, len)
  const pts = normalizeLocalPoints(points, len)
  if (!pts.length) return lt

  if (mode === 'step') {
    const getAffects = (p) => (p?.affects === 'right' ? 'right' : 'left')
    const boundaries = [0, ...pts.map((p) => p.t), len]
    let media = 0
    for (let i = 0; i < boundaries.length - 1; i++) {
      const t0 = boundaries[i]
      const t1 = boundaries[i + 1]
      const leftPoint = i > 0 ? pts[i - 1] : null
      const rightPoint = i < pts.length ? pts[i] : null
      let speed = 1
      if (rightPoint && getAffects(rightPoint) === 'left') speed = rightPoint.speed
      else if (leftPoint && getAffects(leftPoint) === 'right') speed = leftPoint.speed
      if (lt <= t0) return media
      const L = Math.max(0, t1 - t0)
      const x = Math.min(L, Math.max(0, lt - t0))
      media += x * speed
      if (lt <= t1) return media
    }
    return media
  }

  let media = 0
  // Head [0, p0]
  if (pts[0].t > 0) {
    if (lt <= pts[0].t) return integrateLinear(pts[0].speed, pts[0].speed, pts[0].t, lt)
    media += integrateLinear(pts[0].speed, pts[0].speed, pts[0].t, pts[0].t)
  }
  // Middles [pi, p(i+1)]
  for (let i = 0; i < pts.length - 1; i++) {
    const left = pts[i]
    const right = pts[i + 1]
    const span = right.t - left.t
    if (lt <= right.t) return media + integrateLinear(left.speed, right.speed, span, lt - left.t)
    media += integrateLinear(left.speed, right.speed, span, span)
  }
  // Tail [plast, len]
  const last = pts[pts.length - 1]
  if (lt <= len) return media + (lt - last.t) * last.speed
  return media + (len - last.t) * last.speed
}

const mapCheckpoints = (clipLength, normalizedSamples) => {
  const samples = Array.isArray(normalizedSamples) && normalizedSamples.length
    ? normalizedSamples
    : [0, 0.25, 0.5, 0.75, 1]
  return samples.map((u) => clamp(Number(u) || 0, 0, 1) * clipLength)
}

export const SPEED_PHASE0_TERMS = Object.freeze({
  sourceTime: 'Original media-domain time. This is the baseline reference.',
  localClipTime: 'Time inside one timeline clip after timeline placement.',
  speedPercent: 'UI-friendly speed value where 100% = 1.0x.',
  speedMultiplier: 'Runtime speed scalar. speedMultiplier = speedPercent / 100.',
  globalDuration: 'Timeline clip duration after speed/remap is applied.',
  localCurveSpace: 'Normalized [0..1] marker space local to a clip.',
  velocityCurve: 'Speed function m(t) over local clip time.',
  timeMap: 'Integrated mapping from local clip time to source media time.',
})

export const SPEED_PHASE0_ACCEPTANCE = Object.freeze([
  {
    id: 'p0-constant-from-source',
    title: 'Constant speed is absolute from source',
    expectation:
      'Changing a clip to N% should resolve from original source duration, not recursively from previous speed edits.',
  },
  {
    id: 'p0-ramp-duration-from-curve',
    title: 'Ramp duration derives from speed integral',
    expectation:
      'When speed curve changes, clip duration must be recalculated from integrated curve area.',
  },
  {
    id: 'p0-local-marker-behavior',
    title: 'Markers are local and stable',
    expectation:
      'Speed markers should be representable in normalized local space so trim/split can remap deterministically.',
  },
  {
    id: 'p0-single-time-map',
    title: 'Single authoritative time map',
    expectation:
      'Video and audio should both consume the same local->source mapping contract.',
  },
])

export const SPEED_PHASE0_GOLDEN_SCENARIOS = Object.freeze([
  {
    id: 'constant-050',
    title: 'Constant 50% speed doubles duration',
    type: 'constant',
    sourceDuration: 10,
    speedPercent: 50,
    checkpoints: [0, 0.25, 0.5, 0.75, 1],
  },
  {
    id: 'constant-300',
    title: 'Constant 300% speed thirds duration',
    type: 'constant',
    sourceDuration: 9,
    speedPercent: 300,
    checkpoints: [0, 1 / 3, 2 / 3, 1],
  },
  {
    id: 'ramp-linear-capcut-shape',
    title: 'Linear ramp example (CapCut-like marker profile)',
    type: 'ramp',
    mode: 'linear',
    sourceDuration: 10,
    pointsNormalized: [
      { u: 0.0, speed: 1.0 },
      { u: 0.4, speed: 3.0 },
      { u: 0.7, speed: 0.5 },
      { u: 1.0, speed: 1.0 },
    ],
    checkpoints: [0, 0.2, 0.4, 0.7, 1],
  },
  {
    id: 'ramp-step-plateau',
    title: 'Step ramp example (piecewise constant)',
    type: 'ramp',
    mode: 'step',
    sourceDuration: 12,
    pointsNormalized: [
      { u: 0.2, speed: 1.0 },
      { u: 0.5, speed: 2.0 },
      { u: 0.8, speed: 0.5 },
    ],
    checkpoints: [0, 0.2, 0.5, 0.8, 1],
  },
])

export const SPEED_PHASE7_GOLDEN_SCENARIOS = Object.freeze([
  {
    id: 'phase7-horizontal-left-preserve-media',
    title: 'Horizontal drag preserves affected left segment media',
    type: 'phase7',
    kind: 'horizontal-drag',
    mode: 'step',
    clipLength: 10,
    pointsLocal: [
      { t: 4, speed: 1, affects: 'left' },
    ],
    drag: { index: 0, newT: 6 },
  },
  {
    id: 'phase7-horizontal-right-preserve-media',
    title: 'Horizontal drag preserves affected right segment media',
    type: 'phase7',
    kind: 'horizontal-drag',
    mode: 'step',
    clipLength: 10,
    pointsLocal: [
      { t: 3, speed: 1.5, affects: 'right' },
      { t: 8, speed: 1, affects: 'left' },
    ],
    drag: { index: 0, newT: 5 },
  },
  {
    id: 'phase7-trim-remap-step',
    title: 'Trim remap preserves local->media continuity (step)',
    type: 'phase7',
    kind: 'trim-remap',
    mode: 'step',
    clipLength: 12,
    pointsLocal: [
      { t: 2, speed: 0.8, affects: 'left' },
      { t: 5.5, speed: 2, affects: 'right' },
      { t: 9, speed: 0.6, affects: 'left' },
    ],
    trim: { fromLocal: 2.25, toLocal: 9.75 },
  },
  {
    id: 'phase7-split-remap-step',
    title: 'Split remap preserves child local->media continuity (step)',
    type: 'phase7',
    kind: 'split-remap',
    mode: 'step',
    clipLength: 10,
    pointsLocal: [
      { t: 1.5, speed: 1, affects: 'left' },
      { t: 4, speed: 2.2, affects: 'left' },
      { t: 6.5, speed: 0.7, affects: 'right' },
      { t: 8.5, speed: 1.4, affects: 'left' },
    ],
    splitLocal: 5.25,
  },
])

const evaluateConstantScenario = (scenario) => {
  const sourceDuration = Math.max(EPS, Number(scenario.sourceDuration) || 0)
  const speedMultiplier = toMultiplier(scenario.speedPercent)
  const outputDuration = sourceDuration / speedMultiplier
  const strip = {
    start: 0,
    length: outputDuration,
    timeScale: speedMultiplier,
    retime: { enabled: false, points: [], mode: 'linear' },
  }
  const map = createStripRetimeMap(strip)
  const sampleTimes = mapCheckpoints(outputDuration, scenario.checkpoints)
  const samples = sampleTimes.map((local) => {
    const expected = local * speedMultiplier
    const actual = map.localToMedia(local)
    return {
      local: round(local),
      expectedMedia: round(expected),
      actualMedia: round(actual),
      delta: round(actual - expected),
      pass: Math.abs(actual - expected) <= 1e-4,
    }
  })
  const endMedia = map.localToMedia(outputDuration)
  return {
    id: scenario.id,
    title: scenario.title,
    type: scenario.type,
    sourceDuration: round(sourceDuration),
    outputDurationExpected: round(outputDuration),
    outputDurationActual: round(outputDuration),
    speedMultiplier: round(speedMultiplier),
    coverageExpected: round(sourceDuration),
    coverageActual: round(endMedia),
    pass: Math.abs(endMedia - sourceDuration) <= 1e-4 && samples.every((s) => s.pass),
    samples,
  }
}

const evaluateRampScenario = (scenario) => {
  const sourceDuration = Math.max(EPS, Number(scenario.sourceDuration) || 0)
  const mode = scenario.mode === 'step' ? 'step' : 'linear'
  const pointsNorm = Array.isArray(scenario.pointsNormalized) ? scenario.pointsNormalized : []

  // Solve output duration from integrated normalized curve area:
  // sourceDuration = outputDuration * avgSpeed
  const probeLength = 1
  const probePoints = toLocalPointsFromNormalized(pointsNorm, probeLength)
  const avgSpeed = Math.max(EPS, manualMapLocalToMedia(1, 1, probePoints, mode))
  const outputDuration = sourceDuration / avgSpeed
  const localPoints = toLocalPointsFromNormalized(pointsNorm, outputDuration)

  const strip = {
    start: 0,
    length: outputDuration,
    timeScale: 1,
    retime: {
      enabled: true,
      mode,
      points: localPoints,
    },
  }
  const map = createStripRetimeMap(strip)
  const sampleTimes = mapCheckpoints(outputDuration, scenario.checkpoints)
  const samples = sampleTimes.map((local) => {
    const expected = manualMapLocalToMedia(local, outputDuration, localPoints, mode)
    const actual = map.localToMedia(local)
    return {
      local: round(local),
      expectedMedia: round(expected),
      actualMedia: round(actual),
      delta: round(actual - expected),
      pass: Math.abs(actual - expected) <= 2e-4,
    }
  })
  const endMedia = map.localToMedia(outputDuration)
  return {
    id: scenario.id,
    title: scenario.title,
    type: scenario.type,
    mode,
    sourceDuration: round(sourceDuration),
    outputDurationExpected: round(outputDuration),
    outputDurationActual: round(outputDuration),
    avgSpeedExpected: round(avgSpeed),
    coverageExpected: round(sourceDuration),
    coverageActual: round(endMedia),
    pass: Math.abs(endMedia - sourceDuration) <= 2e-4 && samples.every((s) => s.pass),
    samples,
  }
}

const evaluatePhase7HorizontalDragScenario = (scenario) => {
  const mode = scenario.mode === 'linear' ? 'linear' : 'step'
  const clipLength = Math.max(EPS, Number(scenario.clipLength) || 0)
  const points = normalizeLocalPoints(scenario.pointsLocal || [], clipLength)
  const drag = scenario.drag || {}
  const idx = Math.max(0, Math.min(points.length - 1, Number(drag.index) || 0))
  const beforePoint = points[idx]
  if (!beforePoint) {
    return {
      id: scenario.id,
      title: scenario.title,
      type: scenario.type,
      kind: scenario.kind,
      pass: false,
      reason: 'No point found for drag index',
    }
  }

  const updated = applyHorizontalPointDragLocal({
    points,
    clipLength,
    index: idx,
    newT: Number(drag.newT),
  })
  if (!updated) {
    return {
      id: scenario.id,
      title: scenario.title,
      type: scenario.type,
      kind: scenario.kind,
      pass: false,
      reason: 'Failed to apply drag',
    }
  }

  const afterPoint = updated.points[idx]
  const seg = updated.affectedSegment
  const beforeMedia = seg.affects === 'right'
    ? manualMapLocalToMedia(seg.rightBound, clipLength, points, mode) - manualMapLocalToMedia(seg.oldT, clipLength, points, mode)
    : manualMapLocalToMedia(seg.oldT, clipLength, points, mode) - manualMapLocalToMedia(seg.leftBound, clipLength, points, mode)
  const afterMedia = seg.affects === 'right'
    ? manualMapLocalToMedia(seg.rightBound, clipLength, updated.points, mode) - manualMapLocalToMedia(seg.newT, clipLength, updated.points, mode)
    : manualMapLocalToMedia(seg.newT, clipLength, updated.points, mode) - manualMapLocalToMedia(seg.leftBound, clipLength, updated.points, mode)
  const preserved = Math.abs(afterMedia - beforeMedia) <= 2e-4
  const monotonic = monotonicityCheck({ clipLength, points: updated.points, mode })

  return {
    id: scenario.id,
    title: scenario.title,
    type: scenario.type,
    kind: scenario.kind,
    mode,
    affects: seg.affects,
    beforePoint: {
      t: round(beforePoint.t),
      speed: round(beforePoint.speed),
      affects: pointAffects(beforePoint),
    },
    afterPoint: {
      t: round(afterPoint?.t),
      speed: round(afterPoint?.speed),
      affects: pointAffects(afterPoint),
    },
    preservedMediaBefore: round(beforeMedia),
    preservedMediaAfter: round(afterMedia),
    preservedMediaPass: preserved,
    monotonicPass: monotonic.pass,
    monotonic,
    pass: preserved && monotonic.pass,
  }
}

const evaluatePhase7TrimRemapScenario = (scenario) => {
  const mode = scenario.mode === 'linear' ? 'linear' : 'step'
  const clipLength = Math.max(EPS, Number(scenario.clipLength) || 0)
  const points = normalizeLocalPoints(scenario.pointsLocal || [], clipLength)
  const fromLocal = clamp(Number(scenario.trim?.fromLocal) || 0, 0, clipLength)
  const toLocal = clamp(Number(scenario.trim?.toLocal) || clipLength, fromLocal + EPS, clipLength)
  const remapped = sliceWindowPointsLocal({
    points,
    clipLength,
    fromLocal,
    toLocal,
    mode,
  })
  const continuity = evaluateRemapContinuity({
    originalPoints: points,
    originalLength: clipLength,
    mode,
    fromLocal,
    toLocal,
    remappedPoints: remapped,
  })
  const monotonic = monotonicityCheck({
    clipLength: toLocal - fromLocal,
    points: remapped,
    mode,
  })
  return {
    id: scenario.id,
    title: scenario.title,
    type: scenario.type,
    kind: scenario.kind,
    mode,
    window: {
      fromLocal: round(fromLocal),
      toLocal: round(toLocal),
      newLength: round(toLocal - fromLocal),
    },
    remappedPoints: remapped.map((p) => ({
      t: round(p.t),
      speed: round(p.speed),
      affects: pointAffects(p),
    })),
    continuityPass: continuity.pass,
    continuity,
    monotonicPass: monotonic.pass,
    monotonic,
    pass: continuity.pass && monotonic.pass,
  }
}

const evaluatePhase7SplitRemapScenario = (scenario) => {
  const mode = scenario.mode === 'linear' ? 'linear' : 'step'
  const clipLength = Math.max(EPS, Number(scenario.clipLength) || 0)
  const points = normalizeLocalPoints(scenario.pointsLocal || [], clipLength)
  const splitLocal = clamp(Number(scenario.splitLocal) || (clipLength / 2), EPS, clipLength - EPS)
  const left = sliceWindowPointsLocal({
    points,
    clipLength,
    fromLocal: 0,
    toLocal: splitLocal,
    mode,
  })
  const right = sliceWindowPointsLocal({
    points,
    clipLength,
    fromLocal: splitLocal,
    toLocal: clipLength,
    mode,
  })
  const leftContinuity = evaluateRemapContinuity({
    originalPoints: points,
    originalLength: clipLength,
    mode,
    fromLocal: 0,
    toLocal: splitLocal,
    remappedPoints: left,
  })
  const rightContinuity = evaluateRemapContinuity({
    originalPoints: points,
    originalLength: clipLength,
    mode,
    fromLocal: splitLocal,
    toLocal: clipLength,
    remappedPoints: right,
  })
  const leftMonotonic = monotonicityCheck({ clipLength: splitLocal, points: left, mode })
  const rightMonotonic = monotonicityCheck({ clipLength: clipLength - splitLocal, points: right, mode })
  return {
    id: scenario.id,
    title: scenario.title,
    type: scenario.type,
    kind: scenario.kind,
    mode,
    splitLocal: round(splitLocal),
    left: {
      length: round(splitLocal),
      pointCount: left.length,
      points: left.map((p) => ({ t: round(p.t), speed: round(p.speed), affects: pointAffects(p) })),
      continuityPass: leftContinuity.pass,
      monotonicPass: leftMonotonic.pass,
    },
    right: {
      length: round(clipLength - splitLocal),
      pointCount: right.length,
      points: right.map((p) => ({ t: round(p.t), speed: round(p.speed), affects: pointAffects(p) })),
      continuityPass: rightContinuity.pass,
      monotonicPass: rightMonotonic.pass,
    },
    pass: leftContinuity.pass && rightContinuity.pass && leftMonotonic.pass && rightMonotonic.pass,
  }
}

const evaluatePhase7Scenario = (scenario) => {
  if (scenario.kind === 'horizontal-drag') return evaluatePhase7HorizontalDragScenario(scenario)
  if (scenario.kind === 'trim-remap') return evaluatePhase7TrimRemapScenario(scenario)
  if (scenario.kind === 'split-remap') return evaluatePhase7SplitRemapScenario(scenario)
  return {
    id: scenario.id || 'unknown-phase7',
    title: scenario.title || 'Unknown Phase 7 scenario',
    type: 'phase7',
    pass: false,
    reason: `Unknown phase7 scenario kind: ${scenario.kind || 'n/a'}`,
  }
}

const evaluateScenario = (scenario) => {
  if (scenario.type === 'constant') return evaluateConstantScenario(scenario)
  if (scenario.type === 'ramp') return evaluateRampScenario(scenario)
  if (scenario.type === 'phase7') return evaluatePhase7Scenario(scenario)
  return {
    id: scenario.id || 'unknown',
    title: scenario.title || 'Unknown scenario',
    pass: false,
    reason: 'Unknown scenario type',
  }
}

const scenariosForSuite = (suite = 'phase0') => {
  if (suite === 'phase7') return SPEED_PHASE7_GOLDEN_SCENARIOS
  if (suite === 'all') return [...SPEED_PHASE0_GOLDEN_SCENARIOS, ...SPEED_PHASE7_GOLDEN_SCENARIOS]
  return SPEED_PHASE0_GOLDEN_SCENARIOS
}

export function runSpeedPhase0GoldenScenarios({ verbose = false, suite = 'phase0' } = {}) {
  const selected = scenariosForSuite(suite)
  const results = selected.map((scenario) => evaluateScenario(scenario))
  const passed = results.filter((r) => r.pass).length
  const summary = {
    suite,
    total: results.length,
    passed,
    failed: results.length - passed,
    passRate: round(results.length ? passed / results.length : 0, 4),
  }

  if (verbose && typeof console !== 'undefined') {
    console.table(
      results.map((r) => ({
        id: r.id,
        pass: r.pass,
        source: r.sourceDuration,
        outExpected: r.outputDurationExpected,
        coverageExpected: r.coverageExpected,
        coverageActual: r.coverageActual,
      }))
    )
  }

  return { summary, results }
}

export function runSpeedPhase7GoldenScenarios({ verbose = false } = {}) {
  return runSpeedPhase0GoldenScenarios({ verbose, suite: 'phase7' })
}

export function summarizeStripSpeedModel(strip) {
  if (!strip) return null
  const length = Math.max(EPS, Number(strip.length) || 0)
  const sourceStart = Math.max(0, Number(strip.sourceStart) || 0)
  const sourceDuration = Number(strip.sourceDuration) > 0
    ? Number(strip.sourceDuration)
    : null
  const timeScale = Number.isFinite(Number(strip.timeScale)) && Number(strip.timeScale) > 0
    ? Number(strip.timeScale)
    : 1
  const mode = strip?.retime?.mode === 'step' ? 'step' : 'linear'
  const points = getStripRetimePointsLocal(strip, length)
  const normalizedPoints = getStripRetimePointsNormalized(strip, length)
  const map = createStripRetimeMap({
    start: Number(strip.start) || 0,
    length,
    timeScale,
    retime: {
      enabled: !!strip?.retime?.enabled,
      mode,
      points,
    },
  })

  const sourceCoverage = map.localToMedia(length)
  const avgSpeed = sourceCoverage / length
  const segmentDiagnostics = (mode === 'step')
    ? stepSegmentsFromLocalPoints(length, points).map((s) => ({
      index: s.index,
      t0: round(s.t0),
      t1: round(s.t1),
      length: round(Math.max(0, s.t1 - s.t0)),
      speed: round(s.speed),
      resolvedBy: s.resolvedBy,
    }))
    : linearSegmentsFromLocalPoints(length, points).map((s) => ({
      index: s.index,
      t0: round(s.t0),
      t1: round(s.t1),
      length: round(Math.max(0, s.t1 - s.t0)),
      s0: round(s.s0),
      s1: round(s.s1),
      resolvedBy: s.resolvedBy,
    }))

  return {
    length: round(length),
    start: round(Number(strip.start) || 0),
    sourceStart: round(sourceStart),
    sourceDuration: round(sourceDuration != null ? sourceDuration : sourceCoverage),
    timeScale: round(timeScale),
    retimeEnabled: !!strip?.retime?.enabled,
    retimeMode: mode,
    pointCount: normalizedPoints.length,
    pointsNormalized: normalizedPoints.map((p) => ({
      u: round(Number(p.u) || 0),
      speed: round(Number(p.speed) || 1),
      affects: p?.affects === 'right' ? 'right' : 'left',
    })),
    pointsLocal: points.map((p) => ({
      t: round(Number(p.t) || 0),
      speed: round(Number(p.speed) || 1),
      affects: p?.affects === 'right' ? 'right' : 'left',
    })),
    segmentCount: segmentDiagnostics.length,
    segments: segmentDiagnostics,
    sourceCoverage: round(sourceCoverage),
    avgSpeed: round(avgSpeed),
    impliedOutputForOrigDuration: round(
      Number(strip.origDuration) > 0 ? Number(strip.origDuration) / Math.max(EPS, avgSpeed) : 0
    ),
  }
}
